/**
 * A route's loaded data, handed from the server's render to the browser.
 *
 * The server files what a route's `loadData` answered with the render's
 * resource values, so the page carries it; the browser's first visit to the
 * same page starts from it instead of asking the services again. A later
 * visit loads it afresh, as the data may have changed since.
 */
import { currentContext } from '@crown/framework/context';
import { resourceKey, seededValue } from '@crown/framework/data';
import {
  isBoolean,
  isNumber,
  isPlainObject,
  isRecord,
  isString,
  type JsonValue,
} from '@crown/framework/ir';
import { loadedDataKey, type RouteParams } from './match';

/** The value as JSON, with fields that are absent (undefined) left out; null when it holds something JSON cannot carry. */
function asJson<T>(value: T): { readonly json: JsonValue } | null {
  if (value === null) return { json: null };
  if (isString(value) || isBoolean(value)) return { json: value };
  if (isNumber(value)) return Number.isFinite(value) ? { json: value } : null;
  if (Array.isArray(value)) {
    const items: JsonValue[] = [];
    for (const item of value.values()) {
      const converted = asJson(item);
      if (!converted) return null;
      items.push(converted.json);
    }
    return { json: items };
  }
  if (!isRecord(value) || !isPlainObject(value)) return null;
  const fields: { [key: string]: JsonValue } = {};
  for (const [key, field] of Object.entries(value)) {
    if (field === undefined) continue;
    const converted = asJson(field);
    if (!converted) return null;
    fields[key] = converted.json;
  }
  return { json: fields };
}

/** On the server: files what `routePath`'s loader answered, for the browser to start from. */
export function fileLoadedData<T>(
  routePath: string,
  params: RouteParams,
  data: T,
): void {
  const ctx = currentContext();
  if (!ctx) return;
  const converted = asJson(data);
  if (!converted) {
    console.warn(
      `[Router] the data ${routePath} loaded is not JSON (a date, a map or a class instance), so the browser loads it again`,
    );
    return;
  }
  ctx.resources.put(resourceKey(loadedDataKey(routePath, params)), {
    state: 'ready',
    value: converted.json,
  });
}

/** Keys the browser has already started from: each is used once. */
const taken = new Set<string>();

/** In the browser: what the server loaded for this page, the first time it is asked; null after, or when it loaded nothing. */
export function takeLoadedData(
  routePath: string,
  params: RouteParams,
): { readonly value: JsonValue } | null {
  const key = loadedDataKey(routePath, params);
  const text = resourceKey(key);
  if (taken.has(text)) return null;
  const value = seededValue(key);
  if (value === undefined) return null;
  taken.add(text);
  return { value };
}
